import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, API, formatCOP } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Plus } from "lucide-react";

const LOW = 0.6;

const LowBadge = ({ conf, field }) => (conf[field] !== undefined && conf[field] < LOW) ? (
  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full ml-2">
    <AlertTriangle className="w-3 h-3" /> Baja confianza
  </span>
) : null;

export default function ReviewModal({ id, onClose }) {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/remisiones/${id}`).then((res) => setR(res.data)).catch(() => onClose());
    // eslint-disable-next-line
  }, [id]);

  if (!r) return null;

  const conf = r.confianza || {};
  const set = (patch) => setR({ ...r, ...patch });
  const setItem = (i, patch) => {
    const items = [...(r.items || [])];
    items[i] = { ...items[i], ...patch };
    items[i].total = (Number(items[i].cantidad) || 0) * (Number(items[i].precio_unitario) || 0);
    set({ items, total: items.reduce((a, b) => a + (b.total || 0), 0) });
  };
  const addItem = () => set({ items: [...(r.items || []), { producto: "", cantidad: 0, precio_unitario: 0, total: 0 }] });
  const removeItem = (i) => {
    const items = [...(r.items || [])];
    items.splice(i, 1);
    set({ items, total: items.reduce((a, b) => a + (b.total || 0), 0) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/remisiones/${id}`, {
        fecha: r.fecha, vendedor: r.vendedor, cliente: r.cliente,
        ciudad: r.ciudad, metodo_pago: r.metodo_pago,
        items: r.items, total: r.total, estado: "confirmada",
      });
      toast.success("Remision confirmada");
      onClose();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally { setSaving(false); }
  };

  const token = localStorage.getItem("atlas_token");
  const imgUrl = r.storage_path ? `${API}/remisiones/${id}/image?auth=${token}` : null;

  const LowBadge = ({ field }) => (conf[field] !== undefined && conf[field] < LOW) ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full ml-2">
      <AlertTriangle className="w-3 h-3" /> Baja confianza
    </span>
  ) : null;

  return (
    <Dialog open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl p-0" data-testid="review-modal">
        <DialogHeader className="px-8 pt-8 pb-2">
          <DialogTitle className="font-display text-2xl">Confirmar datos de la remision</DialogTitle>
          <p className="text-sm text-muted-foreground">Revise los campos resaltados y confirme la informacion.</p>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="bg-muted min-h-[400px] flex items-center justify-center p-6">
            {imgUrl ? (
              <img src={imgUrl} alt="remision" className="max-h-[520px] max-w-full object-contain rounded-lg shadow-sm border border-border" />
            ) : (
              <div className="text-sm text-muted-foreground">Sin imagen adjunta</div>
            )}
          </div>
          <div className="p-8 space-y-4 max-h-[600px] overflow-y-auto">
            <div>
              <Label>Fecha <LowBadge conf={conf} field="fecha" /></Label>
              <Input data-testid="review-fecha" type="date" value={r.fecha || ""} onChange={(e) => set({ fecha: e.target.value })} className="h-11 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Vendedor <LowBadge conf={conf} field="vendedor" /></Label>
                <Input data-testid="review-vendedor" value={r.vendedor || ""} onChange={(e) => set({ vendedor: e.target.value })} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Cliente <LowBadge conf={conf} field="cliente" /></Label>
                <Input data-testid="review-cliente" value={r.cliente || ""} onChange={(e) => set({ cliente: e.target.value })} className="h-11 mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ciudad <LowBadge conf={conf} field="ciudad" /></Label>
                <Input data-testid="review-ciudad" value={r.ciudad || ""} onChange={(e) => set({ ciudad: e.target.value })} className="h-11 mt-1" placeholder="Ej: Bogota" />
              </div>
              <div>
                <Label>Metodo de pago <LowBadge conf={conf} field="metodo_pago" /></Label>
                <Input data-testid="review-metodo-pago" value={r.metodo_pago || ""} onChange={(e) => set({ metodo_pago: e.target.value })} className="h-11 mt-1" placeholder="Ej: Contra entrega" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Productos <LowBadge conf={conf} field="items" /></Label>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="review-add-item"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
              </div>
              <div className="space-y-2">
                {(r.items || []).map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-5 h-10" placeholder="Producto" value={it.producto} onChange={(e) => setItem(i, { producto: e.target.value })} />
                    <Input className="col-span-2 h-10" type="number" placeholder="Cant." value={it.cantidad} onChange={(e) => setItem(i, { cantidad: Number(e.target.value) })} />
                    <Input className="col-span-3 h-10" type="number" placeholder="Precio" value={it.precio_unitario} onChange={(e) => setItem(i, { precio_unitario: Number(e.target.value) })} />
                    <div className="col-span-1 text-xs text-muted-foreground tabular-nums">{formatCOP(it.total)}</div>
                    <button className="col-span-1 text-muted-foreground/70 hover:text-rose-600" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-border flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Total</div>
                <div className="font-display text-2xl font-semibold tabular-nums">{formatCOP(r.total)}</div>
              </div>
              <Button data-testid="review-save" onClick={save} disabled={saving} className="h-12 px-6 bg-primary hover:bg-primary/90">
                {saving ? "Guardando..." : "Confirmar y guardar"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
